import './measureTool.less';
import {isSelectView, isToolControl, Viewer} from 'neuroglancer/viewer';
import {RefCounted} from "neuroglancer/util/disposable";
import {SliceViewPanel} from "neuroglancer/sliceview/panel";
import {vec2} from "gl-matrix";
import {SliceViewProjectionParameters} from "neuroglancer/sliceview/base";
import {MouseEvent} from "react";
import {thousandth_formatter} from "neuroglancer/util/comm_fun_hq";
import {allToolsButton, viewer} from "../../main";
import debounce from "lodash/debounce";

enum OperateStatus {
  default,
  draggingLine,
  draggingPoint,
  movingLine,
}

enum HoverStatus {
  none,
  line,
  endPoint,
}

const measureLineWidth = 1.2;
const measurePointRadius = 3;
const measureTextBoxColor = "#1f2132";
const measureTextBoxPadding = 8;
const measureTextBoxOffsetY = -10;
const measureFont = 'sans-serif';
const measureTextHeight = 10;

function projectToScreenCoord(point: vec2, projectionParameters: SliceViewProjectionParameters): [number, number] {
  let resultPos1 = vec2.transformMat4(vec2.create(), point, projectionParameters.viewProjectionMat);
  return [(1 + resultPos1[0]) * projectionParameters.width / 2, projectionParameters.height - (1 + resultPos1[1]) * projectionParameters.height / 2];
}

function DrawMeasureLine(ctx: CanvasRenderingContext2D, projectionParameters: SliceViewProjectionParameters, measureLine: MeasureLine) {
  let startPos = projectToScreenCoord(measureLine.startPoint, projectionParameters);
  let endPos = projectToScreenCoord(measureLine.endPoint, projectionParameters);

  ctx.strokeStyle = 'red';
  if (measureLine.isSelected) {
    ctx.strokeStyle = 'green';
  }
  if (measureLine.HoverStatus === HoverStatus.line) {
    ctx.strokeStyle = 'yellow';
  }
  ctx.beginPath();
  ctx.fillStyle = 'white';
  ctx.moveTo(startPos[0], startPos[1]);
  ctx.lineTo(endPos[0], endPos[1]);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(startPos[0], startPos[1], measurePointRadius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(endPos[0], endPos[1], measurePointRadius, 0, 2 * Math.PI);
  ctx.fill();
  if (measureLine.HoverStatus === HoverStatus.endPoint) {
    ctx.strokeStyle = 'yellow';
  }
  ctx.stroke();

  const text = measureLine.distance + ' PX';
  const textMetrics = ctx.measureText(text);

  ctx.beginPath();
  ctx.font = measureTextHeight + 'px ' + measureFont;
  ctx.textAlign = 'center';

  ctx.fillStyle = measureTextBoxColor;
  const totalHeight = measureTextHeight + 2 * measureTextBoxPadding;
  const totalWidth = textMetrics.width + 2 * measureTextBoxPadding;
  // @ts-ignore
  if (ctx.roundRect) {
    // @ts-ignore
    ctx.roundRect(endPos[0], endPos[1] - totalHeight + measureTextBoxOffsetY, totalWidth, totalHeight, 3);
  } else {
    ctx.rect(endPos[0], endPos[1] - totalHeight + measureTextBoxOffsetY, totalWidth, totalHeight);
  }

  ctx.strokeStyle = '#a5a8c2';
  ctx.stroke();
  ctx.fill();
  ctx.fillStyle = 'white';
  ctx.fillText(text, endPos[0] + totalWidth / 2, endPos[1] + measureTextBoxOffsetY + (measureTextHeight - totalHeight) / 2);
}

function drawMeasureLineForPick(ctx: CanvasRenderingContext2D, projectionParameters: SliceViewProjectionParameters, measureLine: MeasureLine) {
  let startPos = projectToScreenCoord(measureLine.startPoint, projectionParameters);
  let endPos = projectToScreenCoord(measureLine.endPoint, projectionParameters);

  ctx.beginPath();
  ctx.strokeStyle = `rgb(255, 0, 0)`;
  ctx.fillStyle = `rgb(0, 255, 0)`;
  ctx.moveTo(startPos[0], startPos[1]);
  ctx.lineTo(endPos[0], endPos[1]);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(endPos[0], endPos[1], measurePointRadius + 4, 0, 2 * Math.PI);
  ctx.fill();
  ctx.restore();
}

class MeasureLine {
  HoverStatus: HoverStatus;
  distance: String | number;
  isSelected: boolean = false;

  constructor(public id: number, public startPoint: vec2, public endPoint: vec2,) {
    this.distance = thousandth_formatter(vec2.distance(startPoint, endPoint).toFixed(0));
  }

  updateDistance() {
    this.distance = thousandth_formatter(vec2.distance(this.startPoint, this.endPoint).toFixed(0));
  }
}

export class MeasureTool extends RefCounted {
  measureCanvas = document.createElement('canvas');//绘制测量的画布
  ctx: CanvasRenderingContext2D | null = this.measureCanvas.getContext('2d');
  hoverCanvas = document.createElement('canvas');//用于判断是否在测量线上的画布，离屏渲染
  hoverCtx: CanvasRenderingContext2D | null = this.hoverCanvas.getContext('2d');
  findIdCanvas = document.createElement('canvas');//用于获取点选测量线ID的画布，离屏渲染
  getIdCtx: CanvasRenderingContext2D | null = this.findIdCanvas.getContext('2d');
  operateStatus: OperateStatus = OperateStatus.default;
  preMousePosition: vec2 = vec2.create();
  hoverMeasureId: number = -1;
  measureLinesDic: { [key: number]: MeasureLine } = {};
  curMeasureId: number = 1;
  mousePickFlag = false;
  fistHoverAfterDown = false;

  constructor(public viewer: Viewer, private viewPanel: SliceViewPanel) {
    super();
    this.initDom();
    this.bindEvent();
  }

  addNewMeasureLine(startPoint: vec2, endPoint: vec2) {
    this.curMeasureId++;
    this.hoverMeasureId = this.curMeasureId;
    this.measureLinesDic[this.curMeasureId] = new MeasureLine(this.curMeasureId, startPoint, endPoint);
    this.updateView();
  }

  initDom() {
    this.measureCanvas.className = 'measure-canvas';
    this.hoverCanvas.className = 'measure-canvas';
    this.measureCanvas.width = this.viewPanel.element.clientWidth;
    this.measureCanvas.height = this.viewPanel.element.clientHeight;
    this.viewPanel.element.appendChild(this.measureCanvas);
    this.updateToolBtnsDisableStatus(false);
  }

  updateToolBtnsDisableStatus(isTurnOffMeasureFunciotn: boolean) {
    const hasCluster = this.viewer.layerManager.getLayerVisible(['cluster']);
    const hasHeatmap = this.viewer.layerManager.getLayerVisible(['heatmap']);
    const can_lasso = (hasCluster && this.viewer.binSize.value === 'cellbin') || (hasHeatmap && this.viewer.binSize.value !== 'cellbin')
    const can_transform = hasHeatmap && allToolsButton.isTransform && !viewer.proteinMergeColor.value && viewer.binSize.value !== 'cellbin'
    const tool_list = isTurnOffMeasureFunciotn && !can_lasso? ['neuroglancer-icon-standardAnalysis', 'lassoBtn'] : ['neuroglancer-icon-standardAnalysis', 'measure_btn']
    if (!can_transform) tool_list.push('neuroglancer-icon-transform');//配准按钮不可点击的情况下，不处理配准按钮状态
    isSelectView(isTurnOffMeasureFunciotn, 'layer-view');//除了layer选择下拉框，其他的都需要处理
    isToolControl(isTurnOffMeasureFunciotn ? 'remove' : 'add', ['neuroglancer-icon-standardAnalysis', 'measure_btn']);
    // if (document.getElementsByClassName('neuroglancer-icon-transform')[0] && document.getElementsByClassName('neuroglancer-icon-transform')[0].getAttribute('data-checked') === 'true') {
    //   (document.getElementsByClassName('neuroglancer-icon-transform')[0] as HTMLElement).classList['remove']("disabled");
    // }
    if (document.getElementsByClassName('more-details-configdialog')[0]) {
      document.body.removeChild((document.getElementsByClassName('more-details-configdialog')[0]));
    }
    let leftToolDoms = document.querySelectorAll('.neuroglancer-viewer-left-row .neuroglancer-icon');
    for (let i = 0; i < leftToolDoms.length; i++) {
      (leftToolDoms[i] as HTMLElement).classList[isTurnOffMeasureFunciotn ? 'remove' : 'add']("disabled");
    }
  }

  clearSelectedLinesState() {
    for (let key in this.measureLinesDic) {
      this.measureLinesDic[key].isSelected = false;
    }
    this.updateView();
  }

  deleteSelectedLines() {
    for (let key in this.measureLinesDic) {
      if (this.measureLinesDic[key].isSelected) {
        delete this.measureLinesDic[key];
      }
    }
    this.updateView();
  }

  bindEvent() {
    this.registerDisposer(this.viewer.navigationState.zoomFactor.changed.add(() => {
      this.updateView();
    }));
    this.registerDisposer(this.viewer.navigationState.position.changed.add(() => {
      this.updateView();
    }));
    this.registerDisposer(this.viewer.navigationState.pose.orientation.changed.add(() => {
      this.updateView();
    }));
    this.registerEventListener(this.viewPanel.element, 'mousemove', this.handleMouseMove.bind(this));
    this.registerEventListener(this.viewPanel.element, 'mousedown', this.handleMouseDown.bind(this));
    this.registerEventListener(this.viewPanel.element, 'mouseup', this.handleMouseUp.bind(this));
    this.registerEventListener(this.viewPanel.element, 'keyup', this.handleDelete.bind(this));
    this.registerEventListener(window, 'resize', debounce(()=>{
      this.updateView();
    }, 100));
  }

  handleMouseUp() {
    if (this.mousePickFlag) {
      if (this.operateStatus === OperateStatus.movingLine) {
        this.hoverMeasureId = -1;
        this.operateStatus = OperateStatus.default;
        this.updateView();
      } else if (this.measureLinesDic[this.hoverMeasureId]) {
        this.measureLinesDic[this.hoverMeasureId].isSelected = !this.measureLinesDic[this.hoverMeasureId].isSelected;
        this.operateStatus = OperateStatus.default;
      } else {
        this.addNewMeasureLine(this.getMouseScreenPos(), this.getMouseScreenPos());
        this.clearSelectedLinesState();
        this.operateStatus = OperateStatus.movingLine;
      }
    } else {
      if (this.operateStatus !== OperateStatus.default) {
        if (this.hoverMeasureId > 0) {
          this.operateStatus = OperateStatus.default;
        }
      }
    }
    this.viewPanel.element.classList.remove('measure');
    this.viewPanel.enablePan = true;
  }

  getMouseScreenPos(): vec2 {
    return vec2.fromValues(this.viewer.mouseState.position[0], this.viewer.mouseState.position[1]);
  }

  handleMouseDown(event: MouseEvent) {
    if (event.button === 0) {
      this.mousePickFlag = true;
      this.fistHoverAfterDown = true;
      this.preMousePosition = vec2.fromValues(this.viewer.mouseState.position[0], this.viewer.mouseState.position[1]);
      if (this.operateStatus === OperateStatus.default && this.hoverMeasureId > 0) {
        if (this.measureLinesDic[this.hoverMeasureId].HoverStatus === HoverStatus.line) {
          this.operateStatus = OperateStatus.draggingLine;
        } else {
          this.operateStatus = OperateStatus.draggingPoint;
        }
        this.measureCanvas.style.pointerEvents = 'none';
        this.viewPanel.element.classList.add('measure');
      }
    }
  }

  handleMouseMove(event: MouseEvent) {
    if (this.fistHoverAfterDown) {//鼠标按下后，会触发一次mouseMove，排除这次的事件干扰
      this.fistHoverAfterDown = false;
      return;
    }
    const curMousePos = vec2.fromValues(this.viewer.mouseState.position[0], this.viewer.mouseState.position[1]);
    const temp = vec2.clone(curMousePos);
    let delta = vec2.sub(curMousePos, curMousePos, this.preMousePosition);
    if ((vec2.length(delta) > 0 && this.hoverMeasureId > 0) || this.hoverMeasureId < 0) {
      this.mousePickFlag = false;
    }
    if (this.operateStatus === OperateStatus.default) {
      if (this.hoverCtx) {
        let data = this.hoverCtx.getImageData(event.pageX, event.pageY, 1, 1);
        if (data.data[3] > 0) {
          let temp = this.getHoverMeasureIdEndType(event.pageX, event.pageY) || -1;
          const hitMeasure = this.measureLinesDic[temp];
          if (hitMeasure) {
            if (this.measureLinesDic[this.hoverMeasureId]) this.measureLinesDic[this.hoverMeasureId].HoverStatus = HoverStatus.none;
            this.hoverMeasureId = temp;
            if (data.data[0] > data.data[1]) {
              hitMeasure.HoverStatus = HoverStatus.line;
            } else {
              hitMeasure.HoverStatus = HoverStatus.endPoint;
            }
            this.measureCanvas.style.pointerEvents = 'unset';
            this.preMousePosition = vec2.fromValues(this.viewer.mouseState.position[0], this.viewer.mouseState.position[1]);
            this.updateView();
          }
        } else {
          if (this.measureLinesDic[this.hoverMeasureId]) {
            this.measureLinesDic[this.hoverMeasureId].HoverStatus = HoverStatus.none;
            this.hoverMeasureId = -1;
            this.measureCanvas.style.pointerEvents = 'none';
            this.updateView();
          }
        }
      }
    } else if (this.operateStatus === OperateStatus.draggingLine) {
      let endPoint = this.measureLinesDic[this.hoverMeasureId].endPoint;
      let startPoint = this.measureLinesDic[this.hoverMeasureId].startPoint;
      vec2.add(endPoint, endPoint, delta);
      vec2.add(startPoint, startPoint, delta);

      this.preMousePosition = temp;
      this.updateView();
    } else if (this.operateStatus === OperateStatus.draggingPoint || this.operateStatus === OperateStatus.movingLine) {
      this.measureLinesDic[this.hoverMeasureId].endPoint = vec2.fromValues(this.viewer.mouseState.position[0], this.viewer.mouseState.position[1]);
      this.measureLinesDic[this.hoverMeasureId].updateDistance();
      this.updateView();
    }
  }

  handleDelete(event: KeyboardEvent) {
    if (event.code === 'Delete') {
      this.deleteSelectedLines();
    }
  }

  updateView() {
    const projectionParameters = this.viewPanel.sliceView.projectionParameters.value;
    if (projectionParameters.width !== this.measureCanvas.width || this.measureCanvas.height !== projectionParameters.height) {
      this.measureCanvas.width = projectionParameters.width;
      this.measureCanvas.height = projectionParameters.height;
    }
    if (this.ctx) {
      const ctx = this.ctx;
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, this.measureCanvas.width, this.measureCanvas.height);
      ctx.lineWidth = measureLineWidth;
      ctx.beginPath();
      for (let key in this.measureLinesDic) {
        DrawMeasureLine(this.ctx, projectionParameters, this.measureLinesDic[key]);
      }
    }
    this.updateOffScreenView();
  }

  getHoverMeasureIdEndType(mouseX: number, mouseY: number) {
    let resultId = -1;
    const projectionParameters = this.viewPanel.sliceView.projectionParameters.value;
    if (projectionParameters.width !== this.hoverCanvas.width || this.hoverCanvas.height !== projectionParameters.height) {
      this.hoverCanvas.width = projectionParameters.width;
      this.hoverCanvas.height = projectionParameters.height;
    }
    if (this.getIdCtx) {
      const ctx = this.getIdCtx;
      ctx.clearRect(0, 0, projectionParameters.width, projectionParameters.height);
      ctx.lineWidth = measureLineWidth + 2;
      for (let key in this.measureLinesDic) {
        const measureLine = this.measureLinesDic[key];
        let startPos = projectToScreenCoord(measureLine.startPoint, projectionParameters);
        let endPos = projectToScreenCoord(measureLine.endPoint, projectionParameters);
        ctx.beginPath();
        ctx.moveTo(startPos[0], startPos[1]);
        ctx.lineTo(endPos[0], endPos[1]);
        ctx.arc(endPos[0], endPos[1], measurePointRadius + 4, 0, 2 * Math.PI);
        ctx.stroke();
        if (ctx.isPointInStroke(mouseX, mouseY)) {
          ctx.beginPath();
          ctx.lineWidth = measureLineWidth + 4;
          ctx.arc(endPos[0], endPos[1], measurePointRadius - 2, 0, 2 * Math.PI);
          ctx.stroke();
          resultId = this.measureLinesDic[key].id;
          break;
        }
      }
    }
    return resultId;
  }

  updateOffScreenView() {
    if (this.operateStatus === OperateStatus.default) {
      const projectionParameters = this.viewPanel.sliceView.projectionParameters.value;
      if (projectionParameters.width !== this.hoverCanvas.width || this.hoverCanvas.height !== projectionParameters.height) {
        this.hoverCanvas.width = projectionParameters.width;
        this.hoverCanvas.height = projectionParameters.height;
      }
      if (this.hoverCtx) {
        const ctx = this.hoverCtx;
        ctx.clearRect(0, 0, projectionParameters.width, projectionParameters.height);
        ctx.lineWidth = measureLineWidth + 4;
        for (let key in this.measureLinesDic) {
          drawMeasureLineForPick(this.hoverCtx, projectionParameters, this.measureLinesDic[key]);
        }
      }
    }
  }

  dispose() {
    super.dispose();
    document.getElementsByClassName('measure_btn')[0]?.setAttribute('data-checked', 'false');
    this.measureCanvas.remove();
    this.updateToolBtnsDisableStatus(true);
    viewer.measureTool = undefined;
  }
}
